"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EventHub = void 0;
const tslib_1 = require("tslib");
const client_utils_1 = require("@sensenet/client-utils");
/**
 * Event hub for sensenet Repository Events
 */
class EventHub {
    constructor(repository) {
        this.repository = repository;
        /**
         * Triggered after a succesful Content creation
         */
        this.onContentCreated = new client_utils_1.ObservableValue();
        /**
         * Triggered after Content creation has been failed
         */
        this.onContentCreateFailed = new client_utils_1.ObservableValue();
        /**
         * Triggered after modifying a Content
         */
        this.onContentModified = new client_utils_1.ObservableValue();
        /**
         * Triggered when failed to modify a Content
         */
        this.onContentModificationFailed = new client_utils_1.ObservableValue();
        /**
         * Triggered when a Content is loaded from the Repository
         */
        this.onContentLoaded = new client_utils_1.ObservableValue();
        /**
         * Triggered when a Content load has been failed
         */
        this.onContentLoadFailed = new client_utils_1.ObservableValue();
        /**
         * Triggered when a collection of content is loaded from the Repository
         */
        this.onContentCollectionLoaded = new client_utils_1.ObservableValue();
        /**
         * Triggered when failed to load a collection of content from the Repository
         */
        this.onContentCollectionLoadFailed = new client_utils_1.ObservableValue();
        /**
         * Triggered after deleting a Content
         */
        this.onContentDeleted = new client_utils_1.ObservableValue();
        /**
         * Triggered after deleting a content has been failed
         */
        this.onContentDeleteFailed = new client_utils_1.ObservableValue();
        /**
         * Triggered after a custom OData Action has been executed
         */
        this.onCustomActionExecuted = new client_utils_1.ObservableValue();
        /**
         * Triggered after a custom OData Action has been failed
         */
        this.onCustomActionFailed = new client_utils_1.ObservableValue();
        /**
         * Triggered after moving a content to another location
         */
        this.onContentMoved = new client_utils_1.ObservableValue();
        /**
         * Triggered after moving a content has been failed
         */
        this.onContentMoveFailed = new client_utils_1.ObservableValue();
        /**
         * Triggered after copying a content to another location
         */
        this.onContentCopied = new client_utils_1.ObservableValue();
        /**
         * Triggered after copying a content has been failed
         */
        this.onContentCopyFailed = new client_utils_1.ObservableValue();
        this.onUploadFinished = new client_utils_1.ObservableValue();
        this.onUploadFailed = new client_utils_1.ObservableValue();
        this.traceObservers = [];
        this.initializeMappings();
    }
    /**
     * Disposes the event hub and all of its ObservableValues
     */
    dispose() {
        for (const key in this) {
            // eslint-disable-next-line no-prototype-builtins
            if (this.hasOwnProperty(key) && typeof this[key].dispose === 'function') {
                // do not dispose repository
                key !== 'repository' && this[key].dispose();
            }
        }
    }
    initializeMappings() {
        this.traceObservers.push(client_utils_1.Trace.method({
            object: this.repository,
            method: this.repository.post,
            methodName: 'post',
            isAsync: true,
            // Post finished to Content Create
            onFinished: (pushPromise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const response = yield pushPromise.returned;
                const content = response.d;
                this.onContentCreated.setValue({
                    content: content,
                });
            }),
            // Post errored to content create failed
            onError: (err) => {
                this.onContentCreateFailed.setValue({
                    content: err.methodArguments[0].content,
                    error: err.error,
                });
            },
        }), client_utils_1.Trace.method({
            object: this.repository,
            method: this.repository.patch,
            methodName: 'patch',
            isAsync: true,
            // Patch finished to ContentModified
            onFinished: (patchPromise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const response = yield patchPromise.returned;
                this.onContentModified.setValue({
                    changes: patchPromise.methodArguments[0].content,
                    content: response.d,
                    forceRefresh: patchPromise.methodArguments[0].forceRefresh,
                });
            }),
            // Patch error to ContentModificationFailed
            onError: (error) => {
                this.onContentModificationFailed.setValue({
                    content: error.methodArguments[0].content,
                    error: error.error,
                });
            },
        }), client_utils_1.Trace.method({
            object: this.repository,
            method: this.repository.put,
            methodName: 'put',
            isAsync: true,
            // Put finished to ContentModified
            onFinished: (putPromise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const response = yield putPromise.returned;
                this.onContentModified.setValue({
                    changes: putPromise.methodArguments[0].content,
                    content: response.d,
                    forceRefresh: putPromise.methodArguments[0].forceRefresh,
                });
            }),
            // Patch error to ContentModificationFailed
            onError: (error) => {
                this.onContentModificationFailed.setValue({
                    content: error.methodArguments[0].content,
                    error: error.error,
                });
            },
        }), client_utils_1.Trace.method({
            object: this.repository,
            method: this.repository.delete,
            methodName: 'delete',
            isAsync: true,
            // handle DeleteBatch finished based on the response value
            onFinished: (deletePromise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const response = yield deletePromise.returned;
                if (response.d.results.length) {
                    this.onContentDeleted.setValue({
                        permanently: deletePromise.methodArguments[0].permanent || false,
                        contentData: response.d.results,
                    });
                }
                if (response.d.errors.length) {
                    for (const failed of response.d.errors) {
                        this.onContentDeleteFailed.setValue({
                            permanently: deletePromise.methodArguments[0].permanent || false,
                            content: failed.content,
                            error: failed.error,
                        });
                    }
                }
            }),
            // Handle DeleteBatch errors
            onError: (error) => {
                let contentArgs = error.methodArguments[0].idOrPath;
                if (!(contentArgs instanceof Array)) {
                    contentArgs = [contentArgs];
                }
                const contents = contentArgs.map((v) => {
                    return isNaN(v) ? { Path: v } : { Id: parseInt(v, 10) };
                });
                for (const c of contents) {
                    this.onContentDeleteFailed.setValue({
                        content: c,
                        permanently: error.methodArguments[0].permanent || false,
                        error: error.error,
                    });
                }
            },
        }), client_utils_1.Trace.method({
            object: this.repository,
            method: this.repository.copy,
            methodName: 'copy',
            isAsync: true,
            // handle CopyBatch finished based on the response value
            onFinished: (copyPromise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const response = yield copyPromise.returned;
                if (response.d.results.length) {
                    this.onContentCopied.setValue({
                        content: response.d.results,
                    });
                }
                if (response.d.errors.length) {
                    for (const failed of response.d.errors) {
                        this.onContentCopyFailed.setValue({
                            content: failed.content,
                            error: failed.error,
                        });
                    }
                }
            }),
            // Handle CopyBatch errors
            onError: (error) => {
                let contentArgs = error.methodArguments[0].idOrPath;
                if (!(contentArgs instanceof Array)) {
                    contentArgs = [contentArgs];
                }
                const contents = contentArgs.map((v) => {
                    return isNaN(v) ? { Path: v } : { Id: parseInt(v, 10) };
                });
                for (const c of contents) {
                    this.onContentCopyFailed.setValue({
                        content: c,
                        error: error.error,
                    });
                }
            },
        }), client_utils_1.Trace.method({
            object: this.repository,
            method: this.repository.move,
            methodName: 'move',
            isAsync: true,
            // handle MoveBatch finished based on the response value
            onFinished: (movePromise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const response = yield movePromise.returned;
                if (response.d.results.length) {
                    this.onContentMoved.setValue({ content: response.d.results });
                }
                if (response.d.errors.length) {
                    for (const failed of response.d.errors) {
                        this.onContentMoveFailed.setValue({
                            content: failed.content,
                            error: failed.error,
                        });
                    }
                }
            }),
            // Handle MoveBatch errors
            onError: (error) => {
                let contentArgs = error.methodArguments[0].idOrPath;
                if (!(contentArgs instanceof Array)) {
                    contentArgs = [contentArgs];
                }
                const contents = contentArgs.map((v) => {
                    return isNaN(v) ? { Path: v } : { Id: parseInt(v, 10) };
                });
                for (const c of contents) {
                    this.onContentMoveFailed.setValue({
                        content: c,
                        error,
                    });
                }
            },
        }), client_utils_1.Trace.method({
            object: this.repository,
            method: this.repository.executeAction,
            methodName: 'executeAction',
            isAsync: true,
            onFinished: (actionPromise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const result = yield actionPromise.returned;
                this.onCustomActionExecuted.setValue({
                    actionOptions: actionPromise.methodArguments[0],
                    oDataParams: actionPromise.methodArguments[0].oDataOptions,
                    result,
                });
            }),
            onError: (error) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                this.onCustomActionFailed.setValue({
                    actionOptions: error.methodArguments[0],
                    error: error.error,
                    oDataParams: error.methodArguments[0].oDataOptions,
                });
            }),
        }), client_utils_1.Trace.method({
            object: this.repository,
            method: this.repository.load,
            methodName: 'load',
            isAsync: true,
            onFinished: (loadPromise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const value = yield loadPromise.returned;
                this.onContentLoaded.setValue({ content: value.d });
            }),
            onError: (error) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                this.onContentLoadFailed.setValue({ payload: error.methodArguments[0], error: error.error });
            }),
        }), client_utils_1.Trace.method({
            object: this.repository,
            method: this.repository.loadCollection,
            methodName: 'loadCollection',
            isAsync: true,
            onFinished: (loadPromise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const value = yield loadPromise.returned;
                this.onContentCollectionLoaded.setValue(value);
            }),
            onError: (error) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                this.onContentCollectionLoadFailed.setValue({ payload: error.methodArguments[0], error: error.error });
            }),
        }), client_utils_1.Trace.method({
            isAsync: true,
            object: this.repository.upload,
            methodName: 'uploadChunked',
            method: this.repository.upload.uploadChunked,
            onFinished: (promise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const returnValue = yield promise.returned;
                this.onUploadFinished.setValue(returnValue);
            }),
            onError: (e) => {
                this.onUploadFailed.setValue({ error: e.error, options: e.methodArguments[0] });
            },
        }), client_utils_1.Trace.method({
            isAsync: true,
            object: this.repository.upload,
            methodName: 'uploadNonChunked',
            method: this.repository.upload.uploadNonChunked,
            onFinished: (promise) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const returnValue = yield promise.returned;
                this.onUploadFinished.setValue(returnValue);
            }),
            onError: (e) => {
                this.onUploadFailed.setValue({ error: e.error, options: e.methodArguments[0] });
            },
        }));
    }
}
exports.EventHub = EventHub;
//# sourceMappingURL=EventHub.js.map